import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Minus, Pencil, Plus, Trash2 } from 'lucide-react';
import {
  ACTION_LABELS,
  MODULE_LABELS,
  PERMISSION_RESOURCES,
  type PermissionAction,
  type PermissionCode,
  type PermissionModule,
  type PermissionResource,
  type Profile,
} from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage, fieldErrors } from '@/lib/errors';
import { T } from '@/design/tokens';
import { WorkspaceHeader } from '@/components/WorkspaceHeader';
import { SmartDataTable, type Column } from '@/components/SmartDataTable';
import { ContextPanel, ReadField } from '@/components/ContextPanel';
import { ActiveBadge, StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/Button';
import { ConfirmDialog, Drawer } from '@/components/Modal';
import { Field, FormSection } from '@/components/FormSection';
import { ErrorState, TableSkeleton } from '@/components/states';
import { useAuth } from '@/lib/auth-context';

const MATRIX_ACTIONS: PermissionAction[] = ['ver', 'criar', 'editar', 'excluir', 'aprovar'];

function groupByModule(): Map<PermissionModule, PermissionResource[]> {
  const byModule = new Map<PermissionModule, PermissionResource[]>();
  for (const resource of PERMISSION_RESOURCES) {
    const bucket = byModule.get(resource.module) ?? [];
    bucket.push(resource);
    byModule.set(resource.module, bucket);
  }
  return byModule;
}

/**
 * Perfis e permissões.
 *
 * A matriz mostra recurso × ação: é a resposta visual para "quem pode aprovar
 * um pagamento?" sem precisar abrir o banco. Perfis do sistema podem ser
 * ajustados mas não excluídos.
 */
export function PerfisPage() {
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [removing, setRemoving] = useState(false);

  const query = useQuery({
    queryKey: ['profiles'],
    queryFn: () => api.get<{ data: Profile[] }>('/profiles'),
  });

  const selected = query.data?.data.find((row) => row.id === selectedId) ?? null;

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['profiles'] });
    void queryClient.invalidateQueries({ queryKey: ['users'] });
  };

  const removeMutation = useMutation({
    mutationFn: (id: string) => api.delete<Profile>(`/profiles/${id}`),
    onSuccess: () => {
      setRemoving(false);
      setSelectedId(null);
      refresh();
    },
  });

  const columns: Array<Column<Profile>> = [
    { key: 'name', label: 'Perfil', strong: true, render: (profile) => profile.name },
    {
      key: 'description',
      label: 'Descrição',
      render: (profile) => (
        <span title={profile.description ?? ''}>
          {profile.description
            ? profile.description.length > 60
              ? `${profile.description.slice(0, 60)}…`
              : profile.description
            : '—'}
        </span>
      ),
    },
    {
      key: 'permissions',
      label: 'Permissões',
      right: true,
      mono: true,
      render: (profile) =>
        profile.fullAccess ? (
          <StatusBadge kind="info">acesso total</StatusBadge>
        ) : (
          String(profile.permissions.length)
        ),
    },
    {
      key: 'users',
      label: 'Usuários',
      right: true,
      mono: true,
      render: (profile) => String(profile.userCount),
    },
    {
      key: 'system',
      label: 'Origem',
      render: (profile) =>
        profile.isSystem ? (
          <StatusBadge kind="mute">do sistema</StatusBadge>
        ) : (
          <StatusBadge kind="ok">personalizado</StatusBadge>
        ),
    },
    {
      key: 'status',
      label: 'Situação',
      render: (profile) => <ActiveBadge active={profile.isActive} />,
    },
  ];

  return (
    <>
      <WorkspaceHeader
        title="Perfis e permissões"
        actions={
          can('perfis:criar') ? (
            <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
              Novo perfil
            </Button>
          ) : undefined
        }
      />

      <div className="workspace__body">
        {query.isPending && <TableSkeleton columns={6} />}
        {query.isError && (
          <ErrorState message={errorMessage(query.error)} onRetry={() => void query.refetch()} />
        )}
        {query.data && (
          <SmartDataTable
            caption="Perfis de acesso"
            columns={columns}
            rows={query.data.data}
            selectedId={selectedId}
            onRowClick={(row) => setSelectedId(row.id)}
          />
        )}
      </div>

      <ContextPanel
        open={Boolean(selected)}
        onClose={() => setSelectedId(null)}
        title={selected?.name ?? ''}
        subtitle={selected?.slug}
        tabs={['Permissões', 'Dados']}
        footer={
          selected ? (
            <>
              {can('perfis:editar') && (
                <Button small variant="primary" icon={Pencil} onClick={() => setEditing(true)}>
                  Editar
                </Button>
              )}
              {can('perfis:excluir') && !selected.isSystem && (
                <Button small variant="danger" icon={Trash2} onClick={() => setRemoving(true)}>
                  Excluir
                </Button>
              )}
            </>
          ) : undefined
        }
      >
        {(tab) =>
          !selected ? null : tab === 0 ? (
            <PermissionMatrix profile={selected} />
          ) : (
            <>
              <ReadField label="Descrição" value={selected.description ?? '—'} />
              <ReadField
                label="Acesso total"
                value={
                  selected.fullAccess
                    ? 'Sim — passa por qualquer ação, inclusive de módulos futuros'
                    : 'Não'
                }
              />
              <ReadField label="Usuários com este perfil" value={String(selected.userCount)} mono />
              <ReadField
                label="Perfil do sistema"
                value={selected.isSystem ? 'Sim — não pode ser excluído' : 'Não'}
              />
            </>
          )
        }
      </ContextPanel>

      <ProfileFormDrawer
        open={creating || (editing && Boolean(selected))}
        profile={editing ? selected : null}
        onClose={() => {
          setCreating(false);
          setEditing(false);
        }}
        onSaved={() => {
          setCreating(false);
          setEditing(false);
          refresh();
        }}
      />

      <ConfirmDialog
        open={removing}
        destructive
        loading={removeMutation.isPending}
        title={selected ? `Excluir o perfil ${selected.name}?` : ''}
        confirmLabel="Excluir perfil"
        message={
          <>
            Só sai um perfil sem usuários. Se alguém ainda o usa, troque o perfil
            dessas pessoas antes.
            {removeMutation.error && (
              <div style={{ color: T.red, marginTop: 10 }}>{errorMessage(removeMutation.error)}</div>
            )}
          </>
        }
        onClose={() => {
          setRemoving(false);
          removeMutation.reset();
        }}
        onConfirm={() => selected && removeMutation.mutate(selected.id)}
      />
    </>
  );
}

function ProfileFormDrawer({
  open,
  profile,
  onClose,
  onSaved,
}: {
  open: boolean;
  profile: Profile | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [fullAccess, setFullAccess] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [permissions, setPermissions] = useState<Set<string>>(new Set());
  const [loadedFor, setLoadedFor] = useState<string | null>(null);

  const formKey = open ? (profile?.id ?? 'new') : null;
  if (formKey !== loadedFor) {
    setLoadedFor(formKey);
    setName(profile?.name ?? '');
    setDescription(profile?.description ?? '');
    setFullAccess(profile?.fullAccess ?? false);
    setIsActive(profile?.isActive ?? true);
    setPermissions(new Set(profile?.permissions ?? []));
  }

  const mutation = useMutation({
    mutationFn: (body: unknown) =>
      profile ? api.patch<Profile>(`/profiles/${profile.id}`, body) : api.post<Profile>('/profiles', body),
    onSuccess: onSaved,
  });

  const errors = fieldErrors(mutation.error);

  const toggle = (code: string) =>
    setPermissions((current) => {
      const next = new Set(current);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });

  // Marcar/limpar um recurso inteiro numa tacada — o operador pensa por linha.
  const toggleResource = (resource: PermissionResource) => {
    const codes = resource.actions.map((action) => `${resource.key}:${action}`);
    const allOn = codes.every((code) => permissions.has(code));
    setPermissions((current) => {
      const next = new Set(current);
      for (const code of codes) {
        if (allOn) next.delete(code);
        else next.add(code);
      }
      return next;
    });
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={profile ? `Editar ${profile.name}` : 'Novo perfil'}
      description="A mudança vale no próximo request de quem usa este perfil"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            loading={mutation.isPending}
            disabled={name.trim().length < 2}
            onClick={() =>
              mutation.mutate({
                name: name.trim(),
                description: description.trim() || null,
                fullAccess,
                isActive,
                permissions: fullAccess ? [] : ([...permissions] as PermissionCode[]),
              })
            }
          >
            {profile ? 'Salvar' : 'Criar perfil'}
          </Button>
        </>
      }
    >
      {mutation.isError && (
        <div className="login__alert" role="alert" style={{ marginBottom: 14 }}>
          {errorMessage(mutation.error)}
        </div>
      )}

      <FormSection>
        <Field label="Nome do perfil" htmlFor="pf-name" error={errors.name}>
          <input id="pf-name" className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Descrição" htmlFor="pf-description" error={errors.description}>
          <input
            id="pf-description"
            className="input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Para quem é este perfil e o que ele cobre"
          />
        </Field>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
          <input
            type="checkbox"
            checked={fullAccess}
            onChange={(e) => setFullAccess(e.target.checked)}
          />
          Acesso total — passa por qualquer ação, inclusive de módulos futuros
        </label>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
          Perfil ativo
        </label>
      </FormSection>

      {!fullAccess && (
        <FormSection title="Permissões" description="Clique no nome do recurso para marcar a linha inteira">
          {[...groupByModule()].map(([module, resources]) => (
            <section key={module} style={{ marginBottom: 18 }}>
              <div className="formsection__title">{MODULE_LABELS[module]}</div>
              <div className="table-wrap">
                <table className="table" style={{ fontSize: 12.5 }}>
                  <thead>
                    <tr>
                      <th>Recurso</th>
                      {MATRIX_ACTIONS.map((action) => (
                        <th key={action} style={{ textAlign: 'center' }}>
                          {ACTION_LABELS[action]}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {resources.map((resource) => (
                      <tr key={resource.key}>
                        <td className="is-strong">
                          <button
                            type="button"
                            onClick={() => toggleResource(resource)}
                            style={{
                              background: 'none',
                              border: 0,
                              padding: 0,
                              font: 'inherit',
                              fontWeight: 600,
                              color: 'inherit',
                              cursor: 'pointer',
                            }}
                            title="Marcar/limpar a linha inteira"
                          >
                            {resource.label}
                          </button>
                        </td>
                        {MATRIX_ACTIONS.map((action) => {
                          const applies = resource.actions.includes(action);
                          const code = `${resource.key}:${action}`;
                          return (
                            <td key={action} style={{ textAlign: 'center' }}>
                              {!applies ? (
                                <span className="muted">·</span>
                              ) : (
                                <input
                                  type="checkbox"
                                  checked={permissions.has(code)}
                                  onChange={() => toggle(code)}
                                  aria-label={`${resource.label}: ${ACTION_LABELS[action]}`}
                                />
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
        </FormSection>
      )}
    </Drawer>
  );
}

function PermissionMatrix({ profile }: { profile: Profile }) {
  if (profile.fullAccess) {
    return (
      <div
        className="attention__item attention__item--info"
        style={{ display: 'flex', width: '100%' }}
      >
        <Check size={15} color={T.mareDark} aria-hidden />
        <span className="attention__text">
          Acesso total: este perfil passa por qualquer ação, inclusive em módulos criados depois.
        </span>
      </div>
    );
  }

  const granted = new Set(profile.permissions);

  return (
    <>
      {[...groupByModule()].map(([module, resources]) => (
        <section key={module} style={{ marginBottom: 22 }}>
          <div className="formsection__title">{MODULE_LABELS[module]}</div>
          <table className="table" style={{ fontSize: 12.5 }}>
            <thead>
              <tr>
                <th>Recurso</th>
                {MATRIX_ACTIONS.map((action) => (
                  <th key={action} style={{ textAlign: 'center' }}>
                    {ACTION_LABELS[action]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {resources.map((resource) => (
                <tr key={resource.key}>
                  <td className="is-strong">{resource.label}</td>
                  {MATRIX_ACTIONS.map((action) => {
                    const applies = resource.actions.includes(action);
                    const allowed = granted.has(`${resource.key}:${action}`);
                    return (
                      <td key={action} style={{ textAlign: 'center' }}>
                        {!applies ? (
                          <span className="muted">·</span>
                        ) : allowed ? (
                          <Check size={14} color={T.green} aria-label="permitido" />
                        ) : (
                          <Minus size={14} color={T.inkFaint} aria-label="não permitido" />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}
    </>
  );
}
